import { makeMockAPICall } from 'models/api';
import { LayerManager } from 'models';
import * as ArcGISManager from './arcgis';
import * as GeoserverManager from './geoserver';

export namespace LegendModel {
    export interface GeoServerPointGraphic {
        mark: string;
        fill: string;
        'fill-opacity': string;
        stroke: string;
        'stroke-width': string;
        'stroke-opacity': string;
        'stroke-linecap': string;
        'stroke-linejoin': string;
        base64: string;
    }
    export interface GeoServerPointSymbol {
        title: string;
        abstract: string;
        url: string;
        size: string;
        opacity: string;
        rotation: string;
        graphics: GeoServerPointGraphic[];
    }

    export interface GeoServerRasterColorMapEntry {
        label?: string;
        quantity: string;
        color: string;
        opacity: string;
    }

    export interface GeoServerRasterColormap {
        entries: GeoServerRasterColorMapEntry[];
        type: string;
    }

    export interface GeoServerRasterSymbol {
        colormap?: GeoServerRasterColormap;
        opacity: string;
    }

    export interface GeoServerLineSymbol {
        stroke: string;
        'graphic-stroke': GeoServerPointSymbol;
        'stroke-width': string;
        'stroke-opacity': string;
        'stroke-linecap': string;
        'stroke-linejoin': string;
        'stroke-dasharray': string[];
        'stroke-dashoffset': string;
    }

    export interface GeoServerPolygonSymbol {
        stroke: string;
        'stroke-width': string;
        'stroke-opacity': string;
        'stroke-linecap': string;
        'stroke-linejoin': string;
        fill: string;
        'fill-opacity': string;
    }

    export interface EsriSymbol {
        label: string;
        contentType: string;
        imageData: string;
    }

    export interface EsriResponseDetails {
        layerId: number;
        layerName: string;
        layerType: string;
        minScale: number;
        maxScale: number;
        legend: EsriSymbol[];
    }

    export interface EsriResponse {
        layers: EsriResponseDetails[];
    }

    export interface EsriGraphic {
        contentType: string;
        imageData: string;
    }

    export interface Entry {
        label?: string;
        quantity: string;
        color: string;
        opacity: string;
    }

    export interface Symbolizer {
        Raster?: GeoServerRasterSymbol;
        Point?: GeoServerPointSymbol;
        Line?: GeoServerLineSymbol;
        Polygon?: GeoServerPolygonSymbol;
        EsriGraphic?: EsriGraphic;
    }

    export interface Rule {
        symbolizers: Symbolizer[];
        title?: string;
        abstract?: string;
    }

    export interface Legend {
        layerName: string;
        title: string;
        rules: Rule[];
    }

    export interface Root {
        Legend: Legend[];
    }
}

const timeBetweenCache = 60 * 60 * 1000; // Every hour
const legendCache: Partial<
    Record<LayerManager.Layer.Id, LegendModel.Legend | null | undefined>
> = {};
const legendCachePromise: Partial<
    Record<
        LayerManager.Layer.Id,
        Promise<LegendModel.Legend> | null | undefined
    >
> = {};
const legendLastUpdateTime: Partial<
    Record<LayerManager.Layer.Id, number | undefined>
> = {};

export const getLegend = makeMockAPICall<
    LegendModel.Legend | null,
    { config: LayerManager.LayerConfig },
    null
>(
    () => {},
    async (_, payload, state) => {
        const { config } = payload;

        if (legendLastUpdateTime[config.id] == null)
            legendLastUpdateTime[config.id] = 0;

        if (
            Date.now() - (legendLastUpdateTime[config.id] ?? 0) >
                timeBetweenCache &&
            state?.auth.tokens.status === 'finished'
        ) {
            if (config.legend) {
                try {
                    legendCache[config.id] = config.legend;
                } catch (_err) {
                    legendCache[config.id] = undefined;
                }
            } else if (legendCachePromise[config.id]) {
                try {
                    legendCache[config.id] =
                        await legendCachePromise[config.id];
                } catch (_err) {
                    legendCache[config.id] = undefined;
                }
            } else {
                if (payload.config.type == 'ImageArcGISRestLayer') {
                    legendCachePromise[config.id] =
                        ArcGISManager.forceGetLegend(payload, state);
                } else {
                    legendCachePromise[config.id] =
                        GeoserverManager.forceGetLegend(payload, state);
                }
                try {
                    legendCache[config.id] =
                        await legendCachePromise[config.id];
                } catch (_err) {
                    legendCache[config.id] = undefined;
                }

                legendCachePromise[config.id] = null;
                legendLastUpdateTime[config.id] = Date.now();
            }
        }
        return legendCache[config.id] ?? null;
    },
    null,
    0,
);
